import {
  ArtButton,
  ArtDialog,
  ArtSelect,
  ArtSwitch,
  ArtTable,
  ArtTableHandle,
  ArtTextField,
  confirmDialog,
  HeadCell,
  SSRTableBuilder,
  useApiService,
} from "@art/shared-ui";
import { faEdit, faPlus, faTrash } from "@fortawesome/free-solid-svg-icons";
import { Box, Chip, Stack, Typography } from "@mui/material";
import { useRef, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { usePageWrapper } from "../../../hooks";
import { useAttributePermission } from "../../permissions";
import { tableParamNav } from "../../../utils/table-utils";

type WorkspaceMode = "personal" | "collaboration" | "governed";
type WorkspaceType = "web" | "aer_desktop";
type WorkspacePlatform = "windows" | "mac" | "linux" | "web";

interface Workspace {
  id: string;
  name: string;
  description?: string;
  lastcheck?: string;
  mode: WorkspaceMode;
  is_active: boolean;
  type: WorkspaceType;
  platform?: WorkspacePlatform;
  owner: string;
  created_at?: string;
}

interface WorkspaceForm {
  name: string;
  description: string;
  mode: WorkspaceMode;
  is_active: boolean;
}

const emptyForm: WorkspaceForm = {
  name: "",
  description: "",
  mode: "personal",
  is_active: true,
};
const endpoint = "/agent-configuration/agent/workspaces";

function Workspaces() {
  const apiService = useApiService();
  const tableRef = useRef<ArtTableHandle>(null);
  const [queryParams, setQueryParams]: any = useSearchParams();
  const [dialogOpen, setDialogOpen] = useState(false);
  const [selected, setSelected] = useState<Workspace | null>(null);
  const [form, setForm] = useState<WorkspaceForm>(emptyForm);
  const [errors, setErrors] = useState<Partial<Record<keyof WorkspaceForm, string>>>({});
  const [saving, setSaving] = useState(false);
  const canCreate = useAttributePermission("ACCOUNT_SETTINGS", "WORKSPACE_MANAGEMENT", "CreateWorkspaces");
  const canEdit = useAttributePermission("ACCOUNT_SETTINGS", "WORKSPACE_MANAGEMENT", "EditWorkspaces");
  const canDelete = useAttributePermission("ACCOUNT_SETTINGS", "WORKSPACE_MANAGEMENT", "DeleteWorkspaces");

  const headCells: HeadCell<Workspace>[] = [
    {
      id: "name",
      numeric: false,
      disablePadding: false,
      label: "Workspace",
      getColData: (row) => (
        <Stack spacing={0.25}>
          <Typography variant="body2" fontWeight={600}>
            {row.name}
          </Typography>
          {row.description && (
            <Typography variant="caption" color="text.secondary">
              {row.description}
            </Typography>
          )}
        </Stack>
      ),
    },
    {
      id: "mode",
      numeric: false,
      disablePadding: false,
      label: "Mode",
      getColData: (row) => (
        <Chip
          size="small"
          variant="outlined"
          color={row.mode === "governed" ? "secondary" : row.mode === "collaboration" ? "primary" : "default"}
          label={row.mode.charAt(0).toUpperCase() + row.mode.slice(1)}
        />
      ),
    },
    {
      id: "platform",
      numeric: false,
      disablePadding: false,
      label: "Platform",
      getColData: (row) => row.platform || "—",
    },
    {
      id: "is_active",
      numeric: false,
      disablePadding: false,
      label: "Status",
      getColData: (row) => (
        <Chip
          size="small"
          color={row.is_active ? "success" : "default"}
          variant={row.is_active ? "filled" : "outlined"}
          label={row.is_active ? "Active" : "Inactive"}
        />
      ),
    },
  ];

  const tableBuilder = SSRTableBuilder<Workspace>({
    endpoint,
    method: "get",
    apiService,
    onFetch: (params) => {
      const page = params.page || "1";
      const searchText = params.search_text || "";
      const nextParams: Record<string, string> = { page };
      if (searchText) nextParams.search = searchText;
      setQueryParams(nextParams, tableParamNav(queryParams, "page", page));
      return { page, limit: "10", search_text: searchText };
    },
    onResponse: (response) => ({
      data: response?.data?.workspaces ?? [],
      total: response?.data?.total_count ?? 0,
    }),
  });

  const openDialog = (workspace?: Workspace) => {
    setSelected(workspace ?? null);
    setForm(
      workspace
        ? {
            name: workspace.name,
            description: workspace.description ?? "",
            mode: workspace.mode,
            is_active: workspace.is_active,
          }
        : emptyForm,
    );
    setErrors({});
    setDialogOpen(true);
  };

  const closeDialog = () => {
    setDialogOpen(false);
    setSelected(null);
    setForm(emptyForm);
    setErrors({});
  };

  const setField = <K extends keyof WorkspaceForm>(key: K, value: WorkspaceForm[K]) => {
    setForm((current) => ({ ...current, [key]: value }));
    setErrors((current) => ({ ...current, [key]: undefined }));
  };

  const validate = () => {
    const next: Partial<Record<keyof WorkspaceForm, string>> = {};
    if (!form.name.trim()) next.name = "Workspace name is required";
    if (form.name.trim().length > 100) next.name = "Workspace name cannot exceed 100 characters";
    setErrors(next);
    return Object.keys(next).length === 0;
  };

  const save = async () => {
    if (!validate()) return;

    setSaving(true);
    try {
      if (selected) {
      const updatePayload = {
        name: form.name.trim(),
        description: form.description.trim(),
        mode: form.mode,
        is_active: form.is_active,
         platform: "web",
      };

      await apiService.put(`${endpoint}/${selected.id}`, updatePayload);
      apiService.showSnack("Workspace updated successfully", "success");
      setDialogOpen(false);
      setSelected(null);
      tableRef.current?.refresh?.();
    } else {
      const createPayload = {
        name: form.name.trim(),
        description: form.description.trim(),
        mode: form.mode,
        is_active: form.is_active,
        platform: "web",
        type: "web" as WorkspaceType,
      };

      await apiService.post(endpoint, createPayload);
      apiService.showSnack("Workspace created successfully", "success");
      setDialogOpen(false);
      setSelected(null);
      tableRef.current?.refresh?.();
    }
    } finally {
      setSaving(false);
    }
  };

  const remove = (workspace: Workspace) => {
    confirmDialog(
      "Delete workspace?",
      `Delete “${workspace.name}”? This action removes it from the active workspace list.`,
    ).then(() => {
      apiService.deleteData(`${endpoint}/${workspace.id}`, {
        onSuccess: () => {
          apiService.showSnack("Workspace deleted successfully", "success");
          tableRef.current?.refresh(0);
        },
        onError: (error: any) => {
          apiService.showSnack(error?.response?.data?.message || "Unable to delete workspace", "error");
        },
      })();
    });
  };

  return (
    <Box className="settings_table_page">
      <Box className="settings_table_header">
        <Typography variant="h6" fontWeight={600} sx={{ mb: 1 }}>
          Workspaces
        </Typography>
        <Typography variant="body2" color="text.secondary">
          Create and manage personal, collaborative, and governed workspaces for the current project and environment.
        </Typography>
      </Box>

      <Box className="settings_table_surface">
        <ArtTable
          ref={tableRef}
          headCells={headCells}
          builder={tableBuilder}
          search={{ enabled: true, value: queryParams.get("search") || "" }}
          pagination={{ initialPage: parseInt(queryParams.get("page") || "1") - 1 }}
          toolbarActions={canCreate ? [
            <ArtButton
              variant="contained"
              dataQa="btn-add-workspace"
              label="Add workspace"
              icon={faPlus}
              onClick={() => openDialog()}
            />,
          ] : []}
          rowActions={[
            canEdit && { icon: faEdit, label: "Edit", onClick: (row: Workspace) => openDialog(row) },
            canDelete && { icon: faTrash, label: "Delete", onClick: (row: Workspace) => remove(row) },
          ].filter(Boolean) as any}
        />
      </Box>

      <ArtDialog
        title={selected ? "Edit workspace" : "Add workspace"}
        open={dialogOpen}
        onClose={closeDialog}
        type=""
        actionBuilder={() => (
          <Box sx={{ display: "flex", gap: 1.5 }}>
            <ArtButton
              buttonType="button"
              dataQa="btn-save-workspace"
              label={saving ? "Saving…" : selected ? "Update" : "Add"}
              disabled={saving}
              onClick={save}
            />
            <ArtButton
              buttonType="button"
              dataQa="btn-cancel-workspace"
              label="Cancel"
              variant="outlined"
              disabled={saving}
              onClick={closeDialog}
            />
          </Box>
        )}
      >
        <Stack spacing={2} sx={{ pt: 0.5, minWidth: { sm: 480 } }}>
          <ArtTextField
            label="Workspace name"
            placeholder="Enter workspace name"
            value={form.name}
            error={errors.name}
            onChange={(event: any) => setField("name", event.target.value)}
            dataQa="input-workspace-name"
            marginBottom={0}
          />
          <ArtTextField
            label="Description"
            placeholder="Describe this workspace"
            value={form.description}
            multiline
            rows={3}
            onChange={(event: any) => setField("description", event.target.value)}
            dataQa="input-workspace-description"
            marginBottom={0}
          />
          <ArtSelect
            id="workspace-mode"
            label="Mode"
            value={form.mode}
            options={[
              { label: "Personal", value: "personal" },
              { label: "Collaboration", value: "collaboration" },
              { label: "Governed", value: "governed" },
            ]}
            onChange={(event: any) => setField("mode", event.target.value as WorkspaceMode)}
            dataQa="select-workspace-mode"
            marginBottom={0}
          />
          <ArtSwitch
            label="Active"
            dataQa="switch-workspace-active"
            required={false}
            error=""
            value={form.is_active}
            onChange={(value: any) =>
              setField("is_active", typeof value === "boolean" ? value : value?.target?.checked)
            }
          />
        </Stack>
      </ArtDialog>
    </Box>
  );
}

export default usePageWrapper(Workspaces, {
  page_title: "Workspaces",
});
